import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { getFingerprint } from "@/lib/fingerprint";
import { setCurrencyGainToastSuppression } from "@/lib/currencyToastGate";

console.log('[AuthContext] Module loaded with fingerprint support');

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null=checking, false=anon, obj=auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("arena_token")) {
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    setCurrencyGainToastSuppression(Boolean(user?.suppress_currency_gain_toasts));
  }, [user?.suppress_currency_gain_toasts]);

  const login = async (email, password) => {
    const fingerprint = await getFingerprint();
    console.log('[Auth] Login with fingerprint:', fingerprint ? fingerprint.substring(0, 20) + '...' : 'NULL');
    const { data } = await api.post("/auth/login", { email, password, fingerprint });
    if (data.twofa_required) return data; // caller must complete the 2FA step
    localStorage.setItem("arena_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const verify2fa = async (challenge, code) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge, code });
    localStorage.setItem("arena_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const ref_code = localStorage.getItem("lanista_ref") || undefined;
    const fingerprint = await getFingerprint();
    console.log('[Auth] Register with fingerprint:', fingerprint ? fingerprint.substring(0, 20) + '...' : 'NULL');
    const { data } = await api.post("/auth/register", { email, password, name, ref_code, fingerprint });
    localStorage.removeItem("lanista_ref");
    localStorage.setItem("arena_token", data.token);
    setUser(data.user);
    return { user: data.user, invited_by: data.invited_by };
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("arena_token");
    setUser(false);
  };

  const refreshUser = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); } catch {}
  };

  const value = useMemo(
    () => ({ user, loading, login, verify2fa, register, logout, refreshUser, setUser }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
